import React, { useState } from 'react';
import { Button, Col, Container, Icon, Loader, Row, SelectInput } from '@digitools/honest-react';
import { DocumentUploadDisplayTextEs } from '../documentUpload/language/DocumentUploadDisplayTextEs';
import { DocumentUploadDisplayTextEn } from './language/DocumentUploadDisplayTextEn';
import styled from 'styled-components';
import useAuthentication from 'src/hooks/useAuthentication';
import useExperience from 'src/hooks/useExperience';
import useTealium from 'src/hooks/useTealium';
import { TEALIUM_EVENT } from 'src/constants/tealiumConstants';
import { CustomEmployeeText } from 'src/utils/CustomExperienceText';
import { Experience } from 'src/types/Experience';
import UploadButton from 'src/components/documentUpload/UploadButton';
import { CategoryCol, StyledSerial } from 'src/components/documentUpload/CategorizationDocumentUpload';
import { docMeta } from 'src/packages/status/components/pages/ClaimDetails/docCategoryMapper';
import SelectCategory from 'src/components/documentUpload/SelectCategory';
import useTranslate from 'src/hooks/useTranslate';
import useCliCustomerConfigContext from 'src/packages/cli/hooks/useCliContext';
import { Language } from 'src/types/Language';
import useDocumentUpload from 'src/components/documentUpload/useDocumentUpload';
import UploadErrorAlerts from 'src/components/documentUpload/UploadErrorAlerts';
// came from CLI
const StyledCol = styled(Col) `
  padding: 12px;
`;
const StyledDeleteButton = styled(Button) `
  font-weight: bold;
  padding: 0;
  height: auto;
  &:hover {
    text-decoration: underline;
  }
`;
const InlineDocumentUpload = ({ productName, documents, setDocuments }) => {
    const { language, experience } = useExperience();
    const { trackEvent } = useTealium();
    // Guard analytics so a Tealium failure can't break the upload success/error flow.
    const safeTrackEvent = (params) => {
        try {
            trackEvent(params);
        }
        catch (error) {
            console.error('Tealium trackEvent failed', error);
        }
    };
    const { authHeader, user } = useAuthentication();
    const languageFile = language === Language.SPANISH ? DocumentUploadDisplayTextEs : DocumentUploadDisplayTextEn;
    const [documentCategory, setDocumentCategory] = useState('');
    const { t } = useTranslate();
    // cli specific
    const { isClaimLeaveCustomer } = useCliCustomerConfigContext();
    //determine if it should see Claim categories or Coordinated categories For CLI
    const isCoordinatedClaim = !!(productName === 'STD' && isClaimLeaveCustomer);
    //product specific logic unrelated to categories
    let tealiumEventType = "";
    let tealiumEventVersion = TEALIUM_EVENT.EVENT_VERSION.REVIEW;
    let documentUploadLabel = "";
    if (productName === 'LIFE') {
        tealiumEventType = TEALIUM_EVENT.EVENT_TYPE.LIFE_CLAIM_INTAKE;
        tealiumEventVersion = 'additional info';
        documentUploadLabel = `${languageFile.informationalMessage1Life}`;
    }
    else if (productName === 'AS') {
        tealiumEventType = TEALIUM_EVENT.EVENT_TYPE.ACCOMMODATION;
        tealiumEventVersion = 'Your accommodation details';
        documentUploadLabel = `${languageFile.informationalMessage1AS}`;
    }
    else {
        tealiumEventType = TEALIUM_EVENT.EVENT_TYPE.CLAIM_LEAVE_INTAKE;
        documentUploadLabel = CustomEmployeeText(experience === Experience.EMPLOYEE
            ? `${languageFile.informationalMessage1} ${languageFile.yourAbsenceEE}`
            : `${languageFile.informationalMessage1} ${languageFile.yourAbsenceER}`);
    }
    let categoryList = docMeta.s1.noCategory;
    if (productName === 'LEAVE') {
        categoryList = docMeta.s1.leaves;
    }
    else if (isCoordinatedClaim) {
        categoryList = (experience === Experience.EMPLOYEE) ? docMeta.s1.coordinatedEE : docMeta.s1.coordinatedER;
    }
    else if (productName === 'STD') {
        if (experience === Experience.EMPLOYEE) {
            categoryList = (user?.customerInContext?.smallMid) ? docMeta.docsNet.claimsEE : docMeta.s1.claimsEE;
        }
        else {
            categoryList = docMeta.s1.claimsER;
        }
    }
    const getDocMeta = (val) => {
        const metaObj = t(categoryList).filter((e) => e.label == val);
        return metaObj[0] || t(docMeta.s1.noCategory)[0];
    };
    const { errorMessage, apiFailureMessage, uploading, onUpload } = useDocumentUpload({
        languageFile,
        interviewType: productName.toUpperCase(),
        // Experience is set to Public for LCI for an unknown reason, so LIFE overrides to Employer.
        // TODO: update LCI component to make sure experience is properly set
        experience: productName === 'LIFE' ? 'Employer' : experience,
        authHeader: authHeader(),
        onUploadSuccess: (response, file) => {
            const meta = getDocMeta(documentCategory);
            setDocuments([
                ...documents,
                {
                    documentName: file.name,
                    docId: response.data,
                    label: meta.label,
                    documentTypeTitle: meta.title,
                    documentType: meta.type,
                },
            ]);
            setDocumentCategory('');
            safeTrackEvent({
                event_action: TEALIUM_EVENT.EVENT_ACTION.UPLOAD,
                event_type: tealiumEventType,
                event_name: 'open',
                event_version: tealiumEventVersion,
                event_results: '1',
            });
        },
        onValidationError: (type, message) => {
            // Empty-file rejections did not previously fire an analytics event.
            if (type === 'empty') {
                return;
            }
            safeTrackEvent({
                event_action: TEALIUM_EVENT.EVENT_ACTION.ERROR,
                event_type: tealiumEventType,
                event_name: message,
                event_version: 'upload document',
            });
        },
        onApiError: () => {
            setDocumentCategory('');
            safeTrackEvent({
                event_action: TEALIUM_EVENT.EVENT_ACTION.ERROR,
                event_type: tealiumEventType,
                event_name: languageFile.apiFailureMessage + languageFile.contactUs,
                event_version: 'upload document',
            });
        },
    });
    const renderColumns = () => {
        return (<>
        <Col lg={6} md={6} sm={6} xs={6}>
          {' '}
          {documents.map((document, index) => {
                if (index <= 4)
                    return getSelectedDocuments(document);
            })}{' '}
        </Col>

        <Col lg={6} md={6} sm={6} xs={6}>
          {' '}
          {documents.map((document, index) => {
                if (index > 4)
                    return getSelectedDocuments(document);
            })}{' '}
        </Col>
      </>);
    };
    const renderCategoryColumns = () => {
        return (<>
        {documents?.map((documentDetail, index) => {
                return (<>
              <CategoryCol md={5}>
                <StyledSerial>{index + 1}</StyledSerial>
                <SelectInput key={`${documentDetail.docId}-${documentDetail.label}`} field={'doc-category'} options={t(categoryList)} data-testid={'doc-category'} initialValue={documentDetail?.label} onChange={(e) => updateCategory(documentDetail, e.target.value)}/>
              </CategoryCol>

              <Col md={7}> {getSelectedDocuments(documentDetail)}</Col>
            </>);
            })}
      </>);
    };
    const updateCategory = (docDetails, val) => {
        let updatedCategory = documents.map(e => {
            let metaDoc = getDocMeta(val);
            if (e.docId == docDetails.docId) {
                return { ...e, label: metaDoc.label, documentTypeTitle: metaDoc.title, documentType: metaDoc.type };
            }
            return e;
        });
        setDocuments(updatedCategory);
    };
    const getSelectedDocuments = (document) => {
        const handleDelete = () => {
            trackEvent({
                event_action: TEALIUM_EVENT.EVENT_ACTION.BUTTON,
                event_type: TEALIUM_EVENT.EVENT_TYPE.DOCUMENT_UPLOAD,
                event_name: 'Delete Uploaded document',
            });
            setDocuments(documents.filter(file => file.docId !== document.docId));
        };
        const colProps = (t(categoryList)).length > 1 ? { md: 8 } : { xs: 12, md: 6, lg: 6 };
        return (<>
        {' '}
        <Row key={document.docId}>
          <StyledCol {...colProps}>
            <Row data-testid={'submitted-doc'} aria-labelledby={document.documentName} data-private={true}>
              <Icon style={{ margin: '2px .5rem 0 1rem' }} name='documents'/> {document.documentName}
            </Row>
          </StyledCol>
          <StyledCol md={3}>
            <StyledDeleteButton data-testid={'delete-doc'} onClick={handleDelete}>
              <Icon color='secondary' name='trash'/> {languageFile.delete}
            </StyledDeleteButton>
          </StyledCol>
        </Row>
      </>);
    };
    const showCategory = (documents?.length < 10 && productName !== 'LIFE' && productName !== 'AS');
    return (<div data-testid='inline-document-upload'>
      <Loader id='document-upload-spinner' isShown={uploading}>
        <Container>
          <UploadErrorAlerts errorMessage={errorMessage} apiFailureMessage={apiFailureMessage} languageFile={languageFile}/>
          <Row data-testid={'informationText'} style={{ marginTop: '1rem' }}>
            {documentUploadLabel}
          </Row>
          <Row data-testid={'acceptableDocumentsText'}>
            {languageFile.informationalMessage2}
          </Row>
          {showCategory ? (<>
                <Row>
                  <p style={{ fontWeight: 'bold', fontSize: 'larger' }}>{languageFile.uploadCategory}</p>
                </Row>
                <Row style={{ marginBottom: '0.5rem' }}>{renderCategoryColumns()}</Row>
                <Row data-testid={'Category'} style={{ marginTop: '1rem' }}>
                  <SelectCategory onUpload={onUpload} eventNum={''} uploadedFiles={documents} documentCategory={documentCategory} uploading={uploading} categoryList={categoryList} setDocumentCategory={setDocumentCategory}/>
                </Row>
              </>) : (<>
                <Row style={{ marginTop: '2rem' }}>
                  <UploadButton onUpload={onUpload} eventNum={''}/>
                </Row>
                <Row style={{ marginBottom: '0.5rem' }}>{renderColumns()}</Row>
              </>)}
        </Container>
      </Loader>
    </div>);
};
export default InlineDocumentUpload;
